import { Fragment, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { OperationVariables, useQuery } from '@apollo/client';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import { TableHead, Divider } from '@mui/material';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Box from '@mui/material/Box';
import TableBody from '@mui/material/TableBody';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useReactToPrint } from 'react-to-print';
import {
  AssessmentType,
  BenchmarkChecklist,
  Checklist,
  StudentChecklistOutcome,
  UnitChecklist,
  WeeklyChecklist,
} from 'types';
import LoadingScreen from '@lwtears/lwt-common-frontend/lib/LoadingScreen';
import { parseQueryParams } from '@lwtears/lwt-common-frontend/lib/@common/util/query-params-util';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import AssessmentScoreLegend from 'components/common/AssessmentScoreLegend';
import OutlineButton from 'components/common/Buttons/OutlineButton';
import { NotepadSrc } from 'components/common/NotepadSrc';
import GET_STUDENT_CHECKLIST_OUTCOMES from 'graph/queries/checklists/get-student-checklist-outcomes';
import useChecklistsStore from 'graph/state/query-hooks/use-checklists';
import { useChecklistStore } from 'graph/state/query-hooks/use-get-checklist';
import useClassroomStore from 'graph/state/query-hooks/use-get-classroom';
import PrintableTable from './components/PrintableTable';
import StudentObservationRow from './components/StudentObservationRow';
import { getCurriculumBenchmarkChecklist } from 'util/checklists-util';

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    mainContainer: {
      paddingLeft: theme.spacing(30),
      paddingRight: theme.spacing(30),
      paddingBottom: theme.spacing(10),
    },
    pageHeader: {
      display: 'flex',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      alignItems: 'flex-end',
      justifyContent: 'space-between',
    },
    tabs: {
      display: 'flex',
    },
    tabItem: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      maxWidth: 'unset',
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      cursor: 'pointer',
    },
    selected: {
      backgroundColor: 'white',
      border: `1px solid ${(theme.palette as any).grey[600]}`,
      borderBottomColor: 'white',
      borderRadius: '10px 10px 0 0',
    },
    secondaryLink: {
      textDecoration: 'none !important',
      color: theme.palette.black.main + ' !important',
      marginLeft: theme.spacing(4),
      fontFamily: 'HWT Futura',
      '&:hover': {
        textDecoration: 'underline !important',
      },
    },
    section: {
      backgroundColor: 'white',
      marginTop: '-1px',
      marginBottom: theme.spacing(8),
      borderTop: `1px solid ${(theme.palette as any).grey[600]}`,
      overflow: 'scroll',
    },
    obsChecklist: {
      '& .MuiTableCell-root': {
        border: `1px solid ${(theme.palette as any).grey[600]}`,
        borderTopColor: 'transparent',
        padding: theme.spacing(2),
      },
    },
  };
});

const ChecklistTable = () => {
  const { id } = useParams();
  const query = parseQueryParams();
  const navigate = useNavigate();

  const reference = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => reference.current,
  });

  const classes = useStyles();

  if (
    typeof id === 'undefined' ||
    query === null ||
    query.classId === null ||
    query.curriculumId === null ||
    isNaN(query.classId) ||
    isNaN(query.curriculumId)
  ) {
    navigate('/404');
  }

  const checklistId = parseInt(id!);
  const classId = parseInt(query.classId);
  const curriculumId = parseInt(query.curriculumId);

  const [checklistStore] = useChecklistStore(checklistId);
  const [checklistsStore] = useChecklistsStore({ curriculumId } as OperationVariables);
  const [classroomStore] = useClassroomStore(classId);

  const studentIds = classroomStore.data?.students?.map((s) => s.id) ?? [];

  const { data: outcomeData, loading: isOutcomeDataLoading } = useQuery<{
    studentChecklistOutcomes: StudentChecklistOutcome[];
  }>(GET_STUDENT_CHECKLIST_OUTCOMES, {
    variables: { where: { checklistId, studentId: studentIds } },
    skip: !studentIds.length,
    fetchPolicy: 'cache-and-network',
  });

  if (
    checklistStore.checklist === undefined ||
    classroomStore.data === undefined ||
    checklistsStore.checklists === undefined ||
    isOutcomeDataLoading
  ) {
    return <LoadingScreen backgroundImage={NotepadSrc} />;
  } else if (
    Object.keys(classroomStore.data).length === 0 ||
    Object.keys(checklistStore.checklist).length === 0
  ) {
    navigate('/404');
  }

  const outcomes = outcomeData?.studentChecklistOutcomes ?? [];

  const getWeeklyTab = (checklist: WeeklyChecklist) => {
    const title = t('class.week') + ' ' + checklist.weekNumber;
    return (
      <Box
        key={'weekly-checklist-tab' + checklist.id}
        onClick={() => {
          navigate(`/checklist/${checklist.id}?classId=${classId}&curriculumId=${curriculumId}`);
        }}
        className={clsx(classes.tabItem, checklist.id === checklistId ? classes.selected : '')}
      >
        <Typography variant="h4">{title}</Typography>
      </Box>
    );
  };

  if (!checklistStore.checklist || !classroomStore.data) {
    navigate('/404');
  }

  const getTitle = (checklist: Checklist) => {
    switch (checklist.type) {
      case AssessmentType.Benchmark: {
        return t(
          `class.benchmarkPeriod-${
            getCurriculumBenchmarkChecklist(
              checklistStore.checklist as BenchmarkChecklist,
              curriculumId
            ).period
          }`
        );
      }
      case AssessmentType.Unit:
      case AssessmentType.Week: {
        return (checklist as UnitChecklist | WeeklyChecklist).unit.title;
      }
      default:
        return '';
    }
  };

  return (
    <section className={classes.mainContainer}>
      <Box>
        <div className={classes.pageHeader}>
          <Typography variant="h1">{getTitle(checklistStore.checklist)}</Typography>
          <Link className={classes.secondaryLink} to={`/class/${classId}?tabSelection=Checklist`}>
            {t('checklist.backToObservationalChecklists')}
          </Link>
        </div>
        <Divider />
        <div className={classes.tabs}>
          {checklistStore.checklist.__typename === 'WeeklyChecklist' &&
            checklistsStore.checklists
              .filter(
                (v) =>
                  v.type === AssessmentType.Week &&
                  (v as WeeklyChecklist).unitId ===
                    (checklistStore.checklist! as WeeklyChecklist).unitId
              )
              .map((checklist) => {
                return getWeeklyTab(checklist as WeeklyChecklist);
              })}
        </div>
        <div style={{ clear: 'both' }}></div>
        <div className={classes.section}>
          <Table className={classes.obsChecklist}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={2}>
                  <OutlineButton onClick={handlePrint}>{t('print')}</OutlineButton>
                  <AssessmentScoreLegend />
                </TableCell>
                {classroomStore.data.students?.map((student) => {
                  return (
                    <TableCell key={'student-' + student.id}>
                      {student.firstName + ' ' + student.lastName}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {checklistStore.checklist.checklistDomains.map((checklistDomain) => {
                return (
                  <Fragment key={'domain-row-' + checklistDomain.domain.id}>
                    <TableRow>
                      <TableCell
                        rowSpan={
                          checklistStore.checklist!.checklistObservations.filter(
                            (v) => v.observation.domainId === checklistDomain.domain.id
                          ).length + 1
                        }
                      >
                        <Typography variant="h4">{checklistDomain.domain.title}</Typography>
                      </TableCell>
                    </TableRow>
                    {checklistStore
                      .checklist!.checklistObservations.filter(
                        (v) => v.observation.domainId === checklistDomain.domain.id
                      )
                      .map((checklistObservation) => {
                        return (
                          <StudentObservationRow
                            key={'student-observation-' + checklistObservation.observation.id}
                            checklistObservation={checklistObservation}
                            students={
                              classroomStore.data!.students ? classroomStore.data!.students : []
                            }
                            outcomes={outcomes.filter(
                              (o) =>
                                o.checklistId === checklistStore.checklist!.id &&
                                o.observationId === checklistObservation.observation.id
                            )}
                            checklistId={checklistStore.checklist!.id}
                          />
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Box>
      <div style={{ display: 'none' }}>
        <PrintableTable
          classroom={classroomStore.data}
          curriculumId={curriculumId}
          checklist={checklistStore.checklist}
          outcomes={outcomes}
          ref={reference}
        />
      </div>
    </section>
  );
};

export default ChecklistTable;
